export type ScenarioNavigation = {
  step: SCENARIO_STEP;
  type: 'internal' | 'external';
};

export enum SummaryLabel {
  LoanAmountAgreement = 'LoanAmountAgreement',
  LoanAmount = 'LoanAmount',
  LoanPerMonth = 'LoanPerMonth',
  LoanTerm = 'LoanTerm',
  LoanRate = 'LoanRate',
  LoanRatePsk = 'LoanRatePsk',
  LoanOverpayment = 'LoanOverpayment',
  LoanInsuranceRate = 'LoanInsuranceRate',
  LoanInsuranceAmount = 'LoanInsuranceAmount',
  LoanPsk = 'LoanPsk',
  LoanPayOffBefore = 'LoanPayOffBefore',
  LoanLimit = 'LoanLimit',
  LoanGracePeriod = 'LoanGracePeriod',
  LoanServiceCost = 'LoanServiceCost',
  LoanInstallmentPeriod = 'LoanInstallmentPeriod',
  LoanTermFromTo = 'LoanTermFromTo',
  LoanPerMonthExact = 'LoanPerMonthExact',
  LoanRateRange = 'LoanRateRange',
  LoanInsurance = 'LoanInsurance',
  InitialPaymentSize = 'InitialPaymentSize',
  LoanRepaymentAmount = 'LoanRepaymentAmount',
  LoanRegularPayment = 'LoanRegularPayment',
  LoanOpeningDate = 'LoanOpeningDate',
  LoanPerMonthWithTerm = 'LoanPerMonthWithTerm',
  Loading = 'Loading',
  LoanRatePskExact = 'LoanRatePskExact',
}

export enum EXTERNAL_SCENARIO_STEP {
  NEXT = 'NEXT',
}

export type Color = 'green' | 'orange' | 'blue' | 'red' | 'dark';

export type Badge = {
  type: 'primary' | 'secondary';
  title: string;
  color: Color;
  icon?: string;
};

export type Summary = {
  value: string;
  valueRaw:
    | number
    | {
        from: number;
        to: number;
      };
  label: string;
  text: string;
  tooltip: string;
};

export type GroupName =
  | 'MplActiveGroup'
  | 'MplPendingGroup'
  | 'MplRefusedGroup'
  | 'SuperRelevant'
  | 'MplReceivedGroup'
  | 'MfoOffers';

export interface StatusStep {
  id: string;
  title: string;
  prev?: string;
  subtitle?: string;
  ordering: number;
  isActive: boolean;
}

export interface StatusBlock {
  title: string;
  stepProgress: StatusStep[];
}

export type ButtonType = 'primary' | 'secondary' | 'text' | 'outlined';

export type TButtonFlow<InteractionClick = never> = {
  label?: string;
  id?: string;
  title?: string;
  onClick: TOnClickFlow | InteractionClick;
  type?: ButtonType;
  disabled?: boolean;
  color?: Color;
};

export type TPayload = {
  amount?: number;
  term?: string;
  agreementId?: string;
  organizationId?: number;
  documentType?: string;
  agreementConfirmCode?: string;
  sendAgreementConfirmCode?: boolean;
} & Record<string, any>;

export type TAction = {
  step: SCENARIO_STEP;
  payload?: TPayload;
  redirectUrl?: string;
};

export type TOnClickFlow = {
  type: string;
  action: TAction;
};

export type OfferInfo = {
  amount: { value: string; valueRaw: number };
  organizationId: number;
  organizationName: string;
  bankOfferId: string;
  organizationLogo: string;
  alias: string;
  productType: string;
  summaries: Summary[];
  onClick: TOnClickFlow;
  buttons?: TButtonFlow[];
  creditCardLogo: string;
};

export type TSummaryValueRaw = {
  from: string | number;
  to: string | number;
};

export type TSummaryValue = {
  from: string;
  to: string;
};

/**
 * Базовый интерфейс для сценариев, который содержит общие поля
 * Step - шаг сценария
 * Content - контент
 * Interactions - если не передали тип для interaction, то interaction не будет в подсказках
 */
export type BaseScenario<Step extends SCENARIO_STEP, Content, Interactions = never> = {
  analytics?: {
    flow?: {
      category?: number;
      subCategory?: number;
    };
    calcId?: string;
  };
  step: Step;
  flowId: string;
  type: 'internal' | 'external';
  redirectUrl?: string;
  content: Content;
  nextStep?: ScenarioNavigation;
  prevStep?: ScenarioNavigation;
} & (Interactions extends never ? {} : { interactions: Interactions });

export enum SCENARIO_STEP {
  BANKS_REFUSED = 'BANKS_REFUSED',
  CREDIT_SELECTION = 'CREDIT_SELECTION',
  CREDIT_OFFERS_LOADING = 'CREDIT_OFFERS_LOADING',
  PRODUCT_ISSUED = 'PRODUCT_ISSUED',
  GO_TO_LEADGEN = 'GO_TO_LEADGEN',
  OFFER_DISBURSEMENT = 'OFFER_DISBURSEMENT',
  CONDITIONS_SELECTION = 'CONDITIONS_SELECTION',
  PLATFORM_CONSENT_ACCEPTANCE = 'PLATFORM_CONSENT_ACCEPTANCE',
}

export interface INextScenarioRequest {
  flow: string;
  step?: SCENARIO_STEP;
  owner: number;
  platform?: string;
  payload?: TPayload & { analytics?: Record<string, unknown> };
}

export interface IStartScenarioRequest {
  scenarioName: string;
  owner: number;
  platform: string;
}

export interface ISendStepScenarioRequest {
  step: SCENARIO_STEP;
}

export interface IStartScenarioResponse {
  item: string;
}
